<template>
  <compnent
    :is="href === '' ? 'button' : 'a'"
    class="oct-icon-button"
    :class="[
      {
        'oct-icon-button--outlined': outline,
        'oct-icon-button--fixed': fix,
      },
      `oct-icon-button--${type}`,
      `oct-icon-button--${size}`
    ]"
    :href="href === '' ? false : href"
  >
    <oct-icon
      :icon="icon"
    />

    <!-- Render help content if helper slot is sat -->
    <div
      class="oct-icon-button-helper"
      v-if="$slots.helper"
      @mouseenter="openPopover(helperId, $event.currentTarget)"
      @mouseleave="closePopover(helperId)"
      @click="$event.stopPropagation()"
    >
      <!-- Help Icon -->
      <oct-icon
        class="oct-icon-button-helper__icon"
        icon="help-sign"
      />

      <!-- Render popover -->
      <oct-popover
        :id="helperId"
      >
        <slot name="helper" />
      </oct-popover>
    </div>
  </compnent>
</template>

<script>
import OctIcon from '@/components/icon/OctIcon.vue'
import OctPopover, {useOctPopover}  from '@/components/popover/OctPopover.vue';

/**
 * Destructures the `open` and `close` methods from the `useOctPopover` composable.
 * 
 * @constant {Function} openPopover - Function to open the popover.
 * @constant {Function} closePopover - Function to close the popover.
 */
const {open: openPopover, close: closePopover} = useOctPopover();

export default {
  components: {
    OctIcon,
    OctPopover
  },
  name: 'OctIconButton',
  props: {
    /** Icon name */
    icon: {
      type: String,
      default: ''
    },
    /** Href */
    href: {
      type: String,
      default: ''
    },
    /** Color [default | primary | on-primary ] */
    type: {
      type: String,
      default: 'default'
    },
    /** Whether outlined */
    outline: {
      type: Boolean,
      default: false
    },
    /** Size [minimum | small | medium | large] */
    size: {
      type: String,
      default: 'medium'
    },
    /** Whether fix the icon size when mouse enters */
    fix: {
      type: Boolean,
      default: true
    },
  },
  computed: {
    /** Generate random ID for the helper popover */
    helperId () {
      return `oct-icon-button-helper--${Math.random().toString(36).substring(2)}`
    }
  },
  methods: {
    openPopover,
    closePopover
  } 
}
</script>

<style scoped lang="scss">
@import "../theme/variables";
@import "../grid/variables";
@import "../typography/variables";
@import "../typography/mixins";

.oct-icon-button {
  fill: $oct-theme--primary;
  margin: 0;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 48px;
  height: 48px;
  padding: 0;
  appearance: none;
  background-color: rgba(0,0,0,.05);
  border: 1.5px solid transparent;
  outline: none;
  border-radius: 50%;
  cursor: pointer;
  box-sizing: border-box;
  -webkit-font-smoothing: antialiased;
  -webkit-tap-highlight-color: rgba(0,0,0,0);

  .oct-icon {
    stroke: $oct-theme--primary;
    width: 30px;
    height: 30px;
  }

  .oct-icon__account-circle,
  .oct-icon__lock,
  .oct-icon__more-horiz,
  .oct-icon__language,
  .oct-icon__instagram,
  .oct-icon__twitter,
  .oct-icon__facebook {
    stroke: none;
  }

  &:hover {
    background-color: rgba(0,0,0,.1);
  }

  /* Cancel hover style on the helper content */
  &:hover:has(.oct-icon-button-helper:hover), &--hover:has(.oct-icon-button-helper:hover) {
    background-color: rgba(0,0,0,.05);
  }

  &--primary {
    background-color: $oct-theme--primary;

    .oct-icon {
      stroke: $oct-theme--on-primary;
    }

    .oct-icon__account-circle,
    .oct-icon__more-lock,
    .oct-icon__more-horiz,
    .oct-icon__language,
    .oct-icon__instagram,
    .oct-icon__twitter,
    .oct-icon__facebook {
      fill: $oct-theme--on-primary;
      stroke: none;
    }

    &:hover {
      background-color: transparent;
      border-color: $oct-theme--primary;

      .oct-icon {
        stroke: $oct-theme--primary;
      }

      .oct-icon__account-circle,
      .oct-icon__more-lock,
      .oct-icon__more-horiz,
      .oct-icon__language,
      .oct-icon__instagram,
      .oct-icon__twitter,
      .oct-icon__facebook {
        fill: $oct-theme--primary;
        stroke: none;
      }
    }

    &:hover:has(.oct-icon-button-helper:hover), &--hover:has(.oct-icon-button-helper:hover) {
      background-color: $oct-theme--primary;
    }

    &:not(.oct-icon-button--fixed):hover {
      width: 56px;
      height: 56px;
    }
  }

  &--primary.oct-icon-button--dense {
    &:not(.oct-icon-button--fixed):hover {
      width: 38px;
      height: 38px;
    }

    &.oct-icon-button--fixed:hover {
      background-color: transparent;
      border-color: $oct-theme--primary;

      .oct-icon {
        stroke: $oct-theme--primary;
      }

      .oct-icon__more-horiz,
      .oct-icon__language,
      .oct-icon__instagram,
      .oct-icon__twitter,
      .oct-icon__facebook {
        fill: $oct-theme--primary;
        stroke: none;
      }
    }
  }

  &--on-primary {
    background-color: $oct-theme--on-primary;

    .oct-icon {
      stroke: $oct-theme--primary;
    }

    .oct-icon__account-circle,
    .oct-icon__lock,
    .oct-icon__more-horiz,
    .oct-icon__language,
    .oct-icon__instagram,
    .oct-icon__twitter,
    .oct-icon__facebook {
      fill: $oct-theme--primary;
      stroke: none;
    }

    &:hover {
      background-color: $oct-theme--primary;
      border-color: $oct-theme--primary;

      .oct-icon {
        stroke: $oct-theme--on-primary;
      }

      .oct-icon__account-circle,
      .oct-icon__lock,
      .oct-icon__more-horiz,
      .oct-icon__language,
      .oct-icon__instagram,
      .oct-icon__twitter,
      .oct-icon__facebook {
        fill: $oct-theme--on-primary;
        stroke: none;
      }
    }

    &:not(.oct-icon-button--fixed):hover {
      width: 56px;
      height: 56px;
    }
  }

  &--outlined {
    border-color: $oct-theme--primary;
    background-color: transparent;

    &:hover {
      background-color: $oct-theme--primary;

      .oct-icon {
        stroke: $oct-theme--on-primary;
      }

      .oct-icon__account-circle,
      .oct-icon__lock,
      .oct-icon__more-horiz,
      .oct-icon__language,
      .oct-icon__instagram,
      .oct-icon__twitter,
      .oct-icon__facebook {
        fill: $oct-theme--on-primary;
        stroke: none;
      }
    }
  }

  &--dense {
    width: oct-rem(32);
    height: oct-rem(32);

    .oct-icon {
      width: oct-rem(20);
      height: oct-rem(20);
    }
  }

  &--minimum {
    width: 24px;
    height: 24px;

    .oct-icon {
      width: 14px;
      height: 14px;
    }
  }

  &--small {
    width: 32px;
    height: 32px;

    .oct-icon {
      width: 20px;
      height: 20px;
    }
  }

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  width: 48px;
  height: 48px;
  border-width: 2px;

  &--primary {
    &:not(.oct-icon-button--fixed):hover {
      width: 72px;
      height: 72px;
    }
  }

  &--primary.oct-icon-button--small {
    &:not(.oct-icon-button--fixed):hover {
      width: 42px;
      height: 42px;
    }
  }

  &--dense {
    width: oct-rem(36);
    height: oct-rem(36);
    border-width: oct-rem(1.5);
  }

  &--minimum {
    width: 24px;
    height: 24px;

    .oct-icon {
      width: 14px;
      height: 14px;
    }
  }

  &--small {
    width: 36px;
    height: 36px;
    border-width: 1.5px;
  }
}
}

/* Helper Content */
.oct-icon-button-helper {
  position: absolute;
  top: 50%;
  left: 100%;
  transform: translateY(-50%);
  margin-left: oct-rem(4);
  display: none;
  cursor: default;

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  display: flex;
}
}

.oct-icon-button .oct-icon-button-helper__icon {
  stroke: none !important;
  width: oct-rem(14);
  height: oct-rem(14);
  opacity: .4;
}
</style>
